import { convertCommasToNumber } from "@/modules/irohana-components/utils/input";

/**
 * Checks if a value is a number.
 *
 * @param {unknown} value - The value to check.
 * @returns {value is number} - True if the value is a number, false otherwise.
 */
export const isNumber = (value: unknown): value is number => {
  return typeof value === "number" && !isNaN(value);
};

/**
 * Converts a value to a number. If the value is not a number, returns 0.
 *
 * @param {unknown} value - The value to convert.
 * @returns {number} - The converted number, or 0 if the value is not a number.
 */
export const toNumber = (value: unknown): number => {
  if (typeof value === "string") {
    // Remove commas from the string before converting
    value = convertCommasToNumber(value);
  }
  const num = Number(value);
  return isNaN(num) || !value ? 0 : num;
};

export const toNumberOrNull = (value: unknown): number | null => {
  return value != null && value !== "" ? toNumber(value) : null;
};

/**
 * Floors a number to two decimal places.
 *
 * @param {number} value - The number to floor.
 * @returns {number} - The floored number with two decimal places.
 */
export const floorToTwoDecimals = (value: number): number => {
  if (!isNumber(value)) return 0;

  return Math.floor(value * 100) / 100;
};

export const roundToTwoDecimals = (value: number): number => {
  if (!isNumber(value)) return 0;
  return Number(value.toFixed(2));
};

export const getCircledNumber = (num: number): string => {
  // ①〜⑳
  if (num >= 1 && num <= 20) {
    return String.fromCharCode(0x2460 + num - 1);
  }

  // ㉑〜㉟ (21〜35)
  if (num >= 21 && num <= 35) {
    return String.fromCharCode(0x3251 + num - 21);
  }

  // fallback
  return `(${num})`;
};
